<template>
  <b-modal v-model="showModal" size="xl" centered @hide="onClose" title="Chi tiết lịch khách hàng" modal-class="patient-detail-modal">
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
    </div>
    <div v-else-if="patientDetail" class="patient-detail-container">
      <b-row class="patient-info-section mb-4">
        <b-col cols="6" class="patient-info d-flex flex-nowrap">
         <div class="mr-8">
           <h5 class="font-weight-bold">{{ patientDetail.account.fullName }}</h5>
            <p class="mb-1">Gói: {{ patientDetail.tranServicePackageName }}</p>
            <p class="mb-1">{{ patientDetail.inWeek }}</p>
            <p class="mb-0">Hoàn thành: {{ patientDetail.completedActivityCount }}</p>
         </div>
          <div class="">
            <h5 class="font-weight-bold">Tương tác trong tuần</h5>
            <p class="mb-1">Xem bài học {{ patientDetail.lessonCount }}</p>
            <p class="mb-1">Nhập chỉ số: {{ patientDetail.inputKPICount }}</p>
            <p class="mb-1">Xem thông báo: {{ patientDetail.checkNotificationCount }}</p>
            <p class="mb-0">Tin nhắn: {{ patientDetail.messageCount }}</p>
         </div>
          <div class="ml-8">
            <h5 class="font-weight-bold">Tương tác trong chương trình</h5>
            <p class="mb-1">Xem bài học: {{ formatProgramInteraction('programLessonCompletedCount', 'programLessonTotalCount', 'programLessonPercent') }}</p>
            <p class="mb-1">Nhập chỉ số: {{ formatProgramInteraction('programInputKPICompletedCount', 'programInputKPITotalCount', 'programInputKPIPercent') }}</p>
            <p class="mb-0">Xem thông báo: {{ formatProgramInteraction('programNotificationReadCount', 'programNotificationTotalCount', 'programNotificationPercent') }}</p>
         </div>
        </b-col>
        <b-col cols="6" class="d-flex align-items-center justify-content-end gap-2">
          <b-button
            class="btn btn-success"
            href="#"
            tabindex="0"
            @click.prevent="onUpdateNow"
            title="Cập nhật dữ liệu ngay"
          >
            <span class="svg-icon">
              <inline-svg src="/media/svg/icons/Neolex/Basic/refresh-cw.svg" />
            </span>
            Cập nhật
          </b-button>
          <b-dropdown
            id="dropdown-filter-detail"
            class="dropdown-form-filter"
            no-caret
            right
          >
            <template #button-content>
              <span class="svg-icon">
                <inline-svg src="/media/svg/icons/Neolex/Basic/filter.svg" />
              </span>
              Bộ lọc
            </template>
            <h6 class="d-flex align-items-center mb-0 p-4">
              <span class="svg-icon mr-3">
                <inline-svg src="/media/svg/icons/Neolex/Basic/filter.svg" />
              </span>
              Bộ lọc
            </h6>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-form>
              <b-container class="p-0">
                <b-row class="mb-3">
                  <b-col cols="4">
                    <basic-select
                      label="Trạng thái"
                      placeholder="Tất cả trạng thái"
                      name="status"
                      data-vv-as="Trạng thái"
                      track-by="value"
                      value-label="text"
                      :value.sync="selectedStatus"
                      :options="statusOptions"
                    />
                  </b-col>
                  <b-col cols="4">
                    <basic-select
                      label="Loại hoạt động"
                      placeholder="Tất cả loại"
                      name="activity-type"
                      data-vv-as="Loại hoạt động"
                      track-by="Id"
                      value-label="Name"
                      :value.sync="selectedActivityType"
                      :options="activityTypes"
                    />
                  </b-col>
                  <b-col cols="4">
                    <basic-date-picker
                      :value.sync="selectedDetailDate"
                      label="Chọn ngày"
                      placeholder="Chọn ngày"
                      name="selectedDetailDate"
                      data-vv-As="Chọn ngày"
                      format="YYYY-MM-DD HH:MM:SS"
                    />
                  </b-col>
                </b-row>
              </b-container>
            </b-dropdown-form>
            <b-dropdown-divider></b-dropdown-divider>
            <div class="d-flex align-items-center justify-content-lg-end m-0 p-4">
              <b-button
                class="btn ml-2"
                href="#"
                tabindex="0"
                @click="onResetFilter"
              >
                <span class="svg-icon">
                  <inline-svg src="/media/svg/icons/Neolex/Basic/refresh-cw.svg" />
                </span>
                Reset bộ lọc
              </b-button>
              <b-button
                class="btn ml-2"
                href="#"
                tabindex="0"
                @click="onApplyFilter"
              >
                <span class="svg-icon">
                  <inline-svg src="/media/svg/icons/Neolex/Basic/filter.svg" />
                </span>
                Lọc dữ liệu
              </b-button>
            </div>
          </b-dropdown>
        </b-col>
      </b-row>

      <div class="calendar-wrapper">
        <table class="detail-calendar-table">
          <thead>
            <tr>
              <th class="header-cell">Hoạt động</th>
              <th 
                v-for="(day, index) in daysOfWeek" 
                :key="index"
                class="day-header"
                :class="{ 'current-day': index === currentDayIndex }"
              >
                {{ day }}<br>{{ currentWeekDates[index] }}
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- Activities Row -->
            <tr class="activities-section">
              <td class="section-label">Lịch hoạt động</td>
              <td 
                v-for="(day, dayIndex) in daysOfWeek" 
                :key="`activity-${dayIndex}`"
                class="day-cell"
                :class="{ 'current-day': dayIndex === currentDayIndex }"
              >
                <div class="event-container">
                  <div
                    v-for="activity in getActivitiesForDay(dayIndex)"
                    :key="activity.id"
                    class="activity-card"
                    :class="{ 'has-reminders': activity.reminders && activity.reminders.length }"
                    :style="{
                      backgroundColor: getActivityColor(activity) || '#16A45A',
                      color: getContrastColor(getActivityColor(activity) || '#16A45A'),
                    }"
                    @click="toggleActivityDetail(activity.id)"
                  >
                    <div class="activity-title-wrapper">
                      <span class="activity-title">{{ activity.name }}</span>
                      <inline-svg
                        v-if="activity.status === 1"
                        :src="'/media/svg/icons/Neolex/Basic/check.svg'"
                        class="activity-check-icon"
                      />
                    </div>
                    <div v-if="activity.reminders && activity.reminders.length && isActivityExpanded(activity.id)" class="reminders-list">
                      <div 
                        v-for="reminder in activity.reminders" 
                        :key="reminder.id"
                        class="reminder-item"
                      >
                        <span class="reminder-time">{{ formatReminderTime(reminder.reminderTime) }}</span>
                        <span class="reminder-method">
                          {{ getReminderMethodText(reminder.activityReminderMethod) }}
                        </span>
                        <span v-if="reminder.isSend" class="sent-badge">
                           <inline-svg
                        v-if="activity.status === 1"
                        :src="'/media/svg/icons/Neolex/Basic/check.svg'"
                        class="activity-check-icon"
                      /> Đã gửi
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>

            <!-- Tracking Row -->
            <tr class="tracking-section">
              <td class="section-label">Tương tác</td>
              <td 
                v-for="(day, dayIndex) in daysOfWeek" 
                :key="`tracking-${dayIndex}`"
                class="day-cell"
                :class="{ 'current-day': dayIndex === currentDayIndex }"
              >
                <div class="event-container">
                  <div
                    v-for="tracking in getTrackingsForDay(dayIndex)"
                    :key="`tracking-${tracking.dateTracking || tracking.firstTracking || tracking.appointmentDate}-${tracking.nameTracking}`"
                    class="tracking-card"
                    :class="{ 'appointment-card': tracking.appointmentDate && !tracking.dateTracking }"
                  >
                    <div class="tracking-title">{{ tracking.nameTracking }}</div>
                    <div v-if="tracking.dateTracking" class="tracking-time">
                      {{ formatTrackingTime(tracking.dateTracking) }}
                    </div>
                    <div v-else-if="tracking.appointmentDate" class="tracking-time appointment-time">
                      <span class="appointment-badge">Lịch hẹn</span>
                      {{ formatAppointmentDate(tracking.appointmentDate) }}
                    </div>
                    <div v-if="tracking.counts !== null" class="tracking-stats">
                      <span class="stat-item">
                        <span class="stat-label">Lần:</span>
                        <span class="stat-value">{{ tracking.counts }}</span>
                      </span>
                    </div>
                    <div v-if="tracking.firstTracking || tracking.lastTracking" class="d-flex justify-content-between">
                      <div v-if="tracking.firstTracking" class="">
                        <span class="time-label">Lần đầu:</span>
                        <span class="fw-bolder">{{ formatAppointmentDate(tracking.firstTracking) }}</span>
                      </div>
                      <div v-if="tracking.lastTracking" class="">
                        <span class="time-label">Lần cuối:</span>
                        <span class="fw-bolder">{{ formatAppointmentDate(tracking.lastTracking) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="onClose">Đóng</b-button>
    </template>
  </b-modal>
</template>

<script>
export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    accountId: {
      type: String,
      default: '',
    },
    coachId: {
      type: String,
      default: '',
    },
    selectedDate: {
      type: [String, Date],
      default: null,
    },
    activityTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showModal: false,
      loading: false,
      patientDetail: null,
      selectedStatus: null,
      selectedActivityType: null,
      selectedDetailDate: null,
      expandedActivities: [],
      statusOptions: [
        { text: 'Tất cả trạng thái', value: null },
        { text: 'Chưa hoàn thành', value: 0 },
        { text: 'Đã hoàn thành', value: 1 },
      ],
      daysOfWeek: [
        'Thứ 2',
        'Thứ 3',
        'Thứ 4',
        'Thứ 5',
        'Thứ 6',
        'Thứ 7',
        'Chủ Nhật',
      ],
    };
  },
  computed: {
    baseDate() {
      if (this.selectedDetailDate) {
        const d = new Date(this.selectedDetailDate);
        if (!isNaN(d.getTime())) return d;
      }
      return new Date();
    },
    currentDayIndex() {
      const today = new Date();
      const todayDayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;
      if (!this.selectedDetailDate) return todayDayIndex;
      const mondayOfSelected = this.getMondayOfWeek(this.baseDate);
      const mondayOfToday = this.getMondayOfWeek(today);
      if (mondayOfSelected.getTime() === mondayOfToday.getTime()) {
        return todayDayIndex;
      }
      const selectedDay = this.baseDate.getDay();
      return selectedDay === 0 ? 6 : selectedDay - 1;
    },
    currentWeekDates() {
      const monday = this.getMondayOfWeek(this.baseDate);
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(monday);
        date.setDate(monday.getDate() + i);
        dates.push(`${date.getDate()}/${date.getMonth() + 1}`);
      }
      return dates;
    },
  },
  watch: {
    visible(val) {
      this.showModal = val;
      if (val) {
        this.selectedDetailDate = this.selectedDate;
        this.fetchAccountDetail();
      }
    },
  },
  methods: {
    getMondayOfWeek(date) {
      const d = new Date(date);
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + diff);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    convertDateToTimestamp(date) {
      if (!date) return null;
      return Math.floor(new Date(date).getTime() / 1000);
    },
    formatProgramInteraction(completedKey, totalKey, percentKey) {
      const completed = this.patientDetail?.[completedKey] || 0;
      const total = this.patientDetail?.[totalKey] || 0;
      const percent = this.patientDetail?.[percentKey] || 0;
      return `${completed}/${total} (${percent}%)`;
    },
    async fetchAccountDetail() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
         console.log('2');
        params.append('accountId', this.accountId);
        if (this.coachId) {
          params.append('coachId', this.coachId);
        }
         console.log('2');
        if (this.selectedDetailDate) {
          const timestamp = this.convertDateToTimestamp(this.selectedDetailDate);
          if (timestamp) {
            params.append('date', timestamp);
          }
        }
        
        if (this.selectedStatus !== null) {
          params.append('status', this.selectedStatus);
        }
        if (this.selectedActivityType) {
          params.append('activityTypeId', this.selectedActivityType);
        }
      console.log('2');
        const url = `/TranUserPackage/Coach/Account-detail?${params.toString()}`;
        const data = await this.$api.get(url);
        if (data && data.length > 0) {
          this.patientDetail = data[0];
        }
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi',
          msg: error.message || 'Không thể tải thông tin chi tiết',
        });
      } finally {
        this.loading = false;
      }
    },
    getActivitiesForDay(dayIndex) {
      if (!this.patientDetail || !this.patientDetail.activities) return [];
      const monday = this.getMondayOfWeek(this.baseDate);
      const targetDate = new Date(monday);
      targetDate.setDate(monday.getDate() + dayIndex);
      const ty = targetDate.getFullYear();
      const tm = targetDate.getMonth() + 1;
      const td = targetDate.getDate();
      return this.patientDetail.activities
        .filter((activity) => {
          if (!activity.tranStartDate) return false;
          const activityDate = new Date(activity.tranStartDate * 1000);
          return (
            activityDate.getFullYear() === ty &&
            activityDate.getMonth() + 1 === tm &&
            activityDate.getDate() === td
          );
        })
        .sort((a, b) => a.tranStartDate - b.tranStartDate);
    },
    getTrackingsForDay(dayIndex) {
      if (!this.patientDetail || !this.patientDetail.userPackageTracking) return [];
      const monday = this.getMondayOfWeek(this.baseDate);
      const targetDate = new Date(monday);
      targetDate.setDate(monday.getDate() + dayIndex);
      const ty = targetDate.getFullYear();
      const tm = targetDate.getMonth() + 1;
      const td = targetDate.getDate();
      return this.patientDetail.userPackageTracking
        .filter((tracking) => {
          const dateTimestamp = tracking.dateTracking || tracking.firstTracking || tracking.appointmentDate;
          if (!dateTimestamp) return false;
          const trackingDate = new Date(dateTimestamp * 1000);
          // Dùng UTC để so sánh vì backend lưu timestamp theo UTC
          // Tránh trường hợp 17:50 UTC ngày 24 bị tính thành 00:50 ngày 25 local (UTC+7)
          return (
            trackingDate.getUTCFullYear() === ty &&
            trackingDate.getUTCMonth() + 1 === tm &&
            trackingDate.getUTCDate() === td
          );
        })
        .sort((a, b) => {
          const dateA = a.dateTracking || a.firstTracking || a.appointmentDate || 0;
          const dateB = b.dateTracking || b.firstTracking || b.appointmentDate || 0;
          return dateA - dateB;
        });
    },
    formatReminderTime(timestamp) {
      const date = new Date(timestamp * 1000);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    },
    formatTrackingTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    },
    formatTrackingDateTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    },
    formatAppointmentDate(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    },
    getReminderMethodIcon(method) {
      return method === 1 ? 'fab fa-zalo' : 'fas fa-mobile-alt';
    },
    getReminderMethodText(method) {
      return method === 1 ? 'Zalo' : 'App';
    },
    getActivityColor(activity) {
      if (!this.activityTypes || this.activityTypes.length === 0) {
        console.warn('activityTypes is empty or undefined');
        return null;
      }
      const type = this.activityTypes.find(t => t.Id === activity.activityTypeId);
      if (!type) {
      }
      return type?.Color || null;
    },
    getContrastColor(hexColor) {
      hexColor = hexColor.replace('#', '');
      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance < 0.5 ? '#FFFFFF' : '#000000';
    },
    toggleActivityDetail(activityId) {
      const index = this.expandedActivities.indexOf(activityId);
      if (index > -1) {
        this.expandedActivities.splice(index, 1);
      } else {
        this.expandedActivities.push(activityId);
      }
    },
    isActivityExpanded(activityId) {
      return this.expandedActivities.includes(activityId);
    },
    onClose() {
      this.showModal = false;
      this.selectedStatus = null;
      this.selectedActivityType = null;
      this.selectedDetailDate = null;
      this.expandedActivities = [];
      this.$emit('close');
    },
    onApplyFilter() {
      this.fetchAccountDetail();
      document.body.click(); 
    },
    onResetFilter() {
      this.selectedStatus = null;
      this.selectedActivityType = null;
      this.selectedDetailDate = null;
      this.fetchAccountDetail();
      document.body.click(); 
    },
    onUpdateNow() {
      this.fetchAccountDetail();
    },
  },
};
</script>

<style scoped>
::v-deep .patient-detail-modal .modal-dialog {
  max-width: 95vw !important;
  width: 95vw !important;
}

::v-deep .patient-detail-modal .modal-body {
  max-height: 80vh;
  overflow-y: auto;
}

::v-deep .dropdown-form-filter .dropdown-menu {
  width: 650px;
}

.svg-icon {
  display: inline-block;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
}

.patient-detail-container {
  padding: 10px;
}

.patient-info-section {
  margin-bottom: 20px;
}

.patient-info {
  padding: 15px;
  border-radius: 4px;
}

.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.gap-2 {
  gap: 0.5rem;
}

.calendar-wrapper {
  width: 100%;
  overflow-x: auto;
}

.detail-calendar-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.header-cell,
.section-label {
  width: 120px;
  background-color: #f5f5f5;
  font-weight: 600;
  padding: 10px;
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;
}

.day-header {
  padding: 10px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
  text-align: center;
  font-size: 14px;
  width: calc((100% - 120px) / 7);
}

.day-cell {
  border: 1px solid #ddd;
  padding: 8px;
  vertical-align: top;
  min-height: 100px;
}

.current-day {
  background-color: #e3f2fd !important;
}

.event-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.activity-card {
  padding: 8px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.activity-card.has-reminders {
  cursor: pointer;
  transition: opacity 0.2s;
}

.activity-card.has-reminders:hover {
  opacity: 0.9;
}

.activity-title-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.activity-title {
  font-weight: 600;
  font-size: 13px;
  flex: 1;
}

.activity-check-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.reminders-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  margin-top: 4px;
}

.reminder-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
}

.reminder-time {
  font-weight: 500;
}

.reminder-method {
  display: flex;
  align-items: center;
  gap: 3px;
}

.sent-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background-color: rgba(255, 255, 255, 0.3);
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  margin-left: auto;
}

.tracking-card {
  background-color: #9e9e9e;
  color: white;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
}


.appointment-badge {
  display: inline-block;
  background-color: rgba(255, 255, 255, 0.25);
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  margin-right: 4px;
  font-weight: 600;
}

.tracking-title {
  font-weight: 500;
  margin-bottom: 4px;
}

.tracking-time {
  font-size: 11px;
  opacity: 0.9;
  margin-bottom: 4px;
}

.tracking-stats {
  font-size: 11px;
  margin-bottom: 4px;
  padding: 4px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-label {
  opacity: 0.9;
}

.stat-value {
  font-weight: 600;
}

.time-label {
  font-weight: 500;
  font-size: 9px;
}

.time-value {
  font-size: 10px;
}

.activities-section td {
  min-height: 150px;
}

.tracking-section td {
  min-height: 100px;
}

@media (max-width: 992px) {
  .detail-calendar-table {
    font-size: 12px;
  }

  .activity-card,
  .tracking-card {
    padding: 6px;
  }

  .reminders-list {
    font-size: 10px;
  }
}
</style>
